import "./RelatedFilesDrawerTable.scss";

import cx from "classnames";
import Table from "components/Table/Table";
import { commonColumnProps, PAGE_SIZE } from "constants/TableConstants";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useGetFilesQuery } from "services/file-api";
import type { FileType } from "types/DataTypes";
import { useSelector } from "utils/redux";

type RelatedFilesDrawerTableProps = {
  fileData?: FileType;
};

type RelatedFileType = {
  id: number;
  claimant_name: string;
  date_of_injury: string;
  services: string;
  status: string;
  status_verbose: string;
};

const RelatedFilesDrawerTable = ({
  fileData,
}: RelatedFilesDrawerTableProps) => {
  const [page, setPage] = useState(1);

  const showRelatedFilesDrawer = useSelector(
    (state) => state.drawer.showRelatedFilesDrawer,
  );

  // get related file IDs from the fileData
  const relatedFileIds =
    (fileData as FileType & { related_files?: number[] })?.related_files || [];

  // filter to exclude the current file from related files
  const filteredRelatedFileIds = relatedFileIds.filter(
    (id) => id !== fileData?.id,
  );

  // fetch files using the id__in filter
  const {
    data: relatedFiles = { results: [], count: 0, next: null, previous: null },
    isFetching,
  } = useGetFilesQuery(
    {
      filters: {
        id__in: filteredRelatedFileIds.join(","),
      },
    },
    {
      skip:
        !fileData?.id ||
        filteredRelatedFileIds.length === 0 ||
        !showRelatedFilesDrawer,
    },
  );

  // transform the data for the table
  const rows = useMemo(() => {
    return relatedFiles.results.map((file) => {
      // handle date_of_injury which can be a string or DateOfInjury object
      let dateOfInjury = "N/A";
      if (file.dates_of_injury && file.dates_of_injury.length > 0) {
        const doi = file.dates_of_injury[0];
        dateOfInjury = typeof doi === "string" ? doi : doi.date;
      }

      return {
        id: file.id,
        claimant_name:
          file.claimants && file.claimants.length > 0
            ? `${file.claimants[0].first_name} ${file.claimants[0].last_name}`
            : "N/A",
        date_of_injury: dateOfInjury,
        services:
          file.services && file.services.length > 0
            ? file.services.map((s) => s.label).join(", ")
            : "None",
        status: file.status,
        status_verbose: file.status_verbose,
      };
    });
  }, [relatedFiles.results]);

  const columns = [
    {
      key: "id",
      name: "File #",
      ...commonColumnProps,
      renderCell: (props: { row: RelatedFileType }) => {
        return (
          <Link
            to={`/operations/files/${props.row.id}/edit/claimant-information`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {props.row.id}
          </Link>
        );
      },
    },
    {
      key: "claimant_name",
      name: "Claimant",
      ...commonColumnProps,
    },
    {
      key: "date_of_injury",
      name: "DOI",
      ...commonColumnProps,
    },
    {
      key: "services",
      name: "Services",
      ...commonColumnProps,
    },
    {
      key: "status_verbose",
      name: "Status",
      ...commonColumnProps,
    },
  ];

  return (
    <div
      className={cx("documents documents-modal flex jc-se", {
        open: showRelatedFilesDrawer,
      })}
    >
      <div className="documents-wrapper">
        <div className="swim-wrapper h-100">
          <div className="swim-headers">Related Files</div>
          <div className="data-values">
            <Table
              className="related-files-table"
              drawer={true}
              columns={columns}
              rows={rows}
              rowKeyGetter={(row: RelatedFileType) => row.id}
              emptyTableMessage="No related files found"
              onCellClick={() => {}}
              isLoading={isFetching}
              pagination={{
                pageSize: PAGE_SIZE,
                pageIndex: page,
                rowCount: filteredRelatedFileIds.length,
                onPageChange: setPage,
                isLoading: isFetching,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelatedFilesDrawerTable;
